import { useState, useRef, useCallback, useEffect } from "react";
import chatAgentService from "../services/RealChatAgentService";
import { getBackendApiUrl } from "../config/backend";
import { vehicleIdentityKey } from "../utils/vehicleIdentity";
import { sanitizeVehicleQuery } from "../utils/sanitizeVehicleQuery";
import { hasLongDigitRun } from "../utils/redactCritical";
import { ymmvOpenFocusDelayMs } from "../utils/ymmvMorph";
import { dismissPhoneKeyboard, isTouchPhone } from "../utils/phoneKeyboard";
import type { Message, MessagePart, SelectorCompletion } from "../types/chat.types";
import type { YMMVMatch, YMMVGroup, TireSizeData } from "../components/chat/VehicleTypeahead";
import type { ActiveVehicle } from "../components/chat/VehicleHeaderBar";

export interface UseYMMVParams {
  // TRAC-732: widened to the shell's 6-param async signature (handoff callers pass
  // metadata/bypass/canned). useYMMV only ever calls it with the first 3 args.
  handleSendMessageRef: React.MutableRefObject<
    ((messageOverride?: string,
      displayOverride?: string,
      skipUserMessage?: boolean,
      metadata?: { selectedValue: string; selectedLabel: string },
      bypass?: boolean,
      canned?: { parts: MessagePart[] }) => Promise<void>) | null
  >;
  upsertVehicle: (vehicle: ActiveVehicle) => void;
  setActiveVehicle: React.Dispatch<React.SetStateAction<ActiveVehicle | null>>;
  pendingVehicleContextRef: React.MutableRefObject<ActiveVehicle | null>;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  isStreaming: boolean;
  setIsStreaming: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  /**
   * TRAC-896: called at the top of every USER-initiated dismissal — the X button
   * and the Escape key — before ymmvMode is torn down. The shell uses it to raise
   * the closing morph's cover. Completion-driven exits (a tire size was picked)
   * deliberately do NOT call it.
   */
  onDismissRequested?: (snapshot: {
    step: "vehicle" | "tire_size";
    vehicle: YMMVMatch | null;
    variant: string | null;
  }) => void;
  /**
   * TRAC-896: fired when the picker COMPLETES (a tire size is committed), with
   * the vehicle the customer settled on and — unless the picker was a fitment
   * QUESTION, where the size is a query rather than a choice — the tire size.
   * The consumer renders it as the customer's answer in the transcript; this
   * hook only reports what was picked.
   */
  onSelectionCommitted?: (vehicleDisplay: string, tireSize?: string) => void;
}

/**
 * TRAC-497: YMMV vehicle-search concern extracted from ChatInterface. Owns YMMV
 * mode state, tire-size picker, drill/breadcrumb stack, and all search/select
 * handlers. Behavior lifted verbatim from post-merge ChatInterface
 * (TRAC-232/378/443/472/698/711 + browse-by-size). The one external call
 * (handleSendMessage from handleTireSizeSelect) routes through
 * handleSendMessageRef to break the definition-order cycle.
 */
export function useYMMV(params: UseYMMVParams) {
  const {
    handleSendMessageRef,
    upsertVehicle,
    setActiveVehicle,
    pendingVehicleContextRef,
    setMessages,
    isStreaming,
    setIsStreaming,
    setIsLoading,
    setInputValue,
    inputRef,
    onDismissRequested,
    onSelectionCommitted,
  } = params;

    const [ymmvMode, setYmmvMode] = useState(false);
    const [ymmvResults, setYmmvResults] = useState<YMMVMatch[]>([]);
    const [ymmvGroups, setYmmvGroups] = useState<YMMVGroup[] | null>(null);
    const [ymmvLoading, setYmmvLoading] = useState(false);
    const [ymmvSelectedIndex, setYmmvSelectedIndex] = useState(-1);
    const [ymmvTotalCount, setYmmvTotalCount] = useState(0);
    const [ymmvGroupType, setYmmvGroupType] = useState<string | null>(null);
    const [ymmvMultiTier, setYmmvMultiTier] = useState(false);
    const [ymmvTotalMatches, setYmmvTotalMatches] = useState(0);
    const [ymmvDroppedYear, setYmmvDroppedYear] = useState<string | null>(null);
    const ymmvDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

    // TRAC-232: Tire size picker (step 2 of YMMV flow)
    const [ymmvStep, setYmmvStep] = useState<"vehicle" | "tire_size">("vehicle");
    const [ymmvSelectedVehicle, setYmmvSelectedVehicle] = useState<YMMVMatch | null>(null);
    const [ymmvTireSizeData, setYmmvTireSizeData] = useState<TireSizeData | null>(null);
    const [ymmvTireSizeLoading, setYmmvTireSizeLoading] = useState(false);
    const [ymmvTireSizeError, setYmmvTireSizeError] = useState(false);
    const [ymmvActiveVariant, setYmmvActiveVariant] = useState<string | null>(null);
    // TRAC-443: breadcrumb stack of prior {query, variant} states the user
    // drilled through via clicks, so a Back button can pop one level instead of
    // forcing them to hand-delete text in the search bar. canGoBack mirrors
    // (stack.length > 0) as state purely to drive the button's visibility.
    const ymmvDrillStackRef = useRef<{ query: string; variant: string | null }[]>([]);
    const [ymmvCanGoBack, setYmmvCanGoBack] = useState(false);
    // TRAC-896: "card" = opened by tapping the inline partial YMMV card.
    // Behaves like "agent" for completion (structured resume, skip user bubble) but like
    // "user" for dismissal (silent close — no "No worries" apology) and never removes
    // the assistant bubble the card sits in.
    const ymmvSourceRef = useRef<"starter" | "agent" | "user" | "card">("starter");
    const ymmvPrefillRef = useRef<string>("");
    // Slice 2 resume (agent-v2): intent the agent tagged the raised picker with; consumed when
    // the pick completes. Set by the shell's ymmv_interface chunk handler.
    const ymmvIntentRef = useRef<string | undefined>(undefined);
    // Slice 2 fitment (agent-v2): size the customer already named on a check_fitment picker; when
    // set, the tire-size step is skipped and this size resumes (refreshed on every agent picker emit).
    const ymmvPrefillSizeRef = useRef<string | undefined>(undefined);
    const ymmvDismissedRef = useRef<boolean>(false); // Prevent duplicate dismiss messages
    const ymmvStartTimeRef = useRef<number | null>(null);
    const [ymmvElapsed, setYmmvElapsed] = useState<number | null>(null);
    const pendingYmmvTimingRef = useRef<{ elapsedMs: number; openedAt: number } | null>(null);
    // Slice 2 resume (agent-v2): structured pick dropped here by handleTireSizeSelect, consumed
    // by the shell's handleSendMessage on the next send.
    const pendingSelectorCompletionRef = useRef<SelectorCompletion | null>(null);
    const allTireSizesRef = useRef<string[] | null>(null);

    // -----------------------------------------------------------------------
    // TRAC-232: YMMV vehicle search mode
    // -----------------------------------------------------------------------

    /** Fetch tire size data for a selected vehicle (with progressive backoff) */
    const fetchTireSizes = useCallback(async (match: YMMVMatch, variant?: string | null, attempt = 0) => {
      const MAX_RETRIES = 3;
      const TIMEOUT_MS = [5000, 10000, 15000];  // progressive: 5s, 10s, 15s
      if (attempt === 0) {
        setYmmvTireSizeLoading(true);
        setYmmvTireSizeError(false);
      }
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS[attempt] ?? 15000);
        let url = `${getBackendApiUrl()}/api/ymmv/tire-sizes?year=${encodeURIComponent(match.year)}&make=${encodeURIComponent(match.make)}&model=${encodeURIComponent(match.model)}`;
        if (variant) url += `&variant=${encodeURIComponent(variant)}`;
        const res = await fetch(url, { signal: controller.signal });
        clearTimeout(timeoutId);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setYmmvTireSizeData(data);
        setYmmvTireSizeLoading(false);
      } catch {
        if (attempt < MAX_RETRIES - 1) {
          return fetchTireSizes(match, variant, attempt + 1);
        }
        // Final failure — show error with retry button
        setYmmvTireSizeError(true);
        setYmmvTireSizeLoading(false);
      }
    }, []);

    /** Search the YMMV index via backend proxy (with progressive timeout + retry).
     *  userClicked: only pass true from explicit click handlers (handleYmmvSelect,
     *  handleYmmvGroupSelect). When false, single-result searches just display
     *  results without advancing to the tire size picker. (TRAC-378) */
    const searchYMMV = useCallback(async (q: string, variant?: string, attempt = 0, fallbackMatch?: YMMVMatch, userClicked = false) => {
      const MAX_RETRIES = 3;
      const TIMEOUT_MS = [5000, 10000, 15000]; // progressive: 5s, 10s, 15s
      // CPOC-140: never send a card number being typed to the vehicle search (any entry point).
      if (hasLongDigitRun(q)) { setYmmvLoading(false); return; }
      if (attempt === 0) setYmmvLoading(true);
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS[attempt] ?? 15000);
        let url = `${getBackendApiUrl()}/api/ymmv/search?q=${encodeURIComponent(q.trim())}`;
        if (variant) url += `&variant=${encodeURIComponent(variant)}`;
        const res = await fetch(url, { signal: controller.signal });
        clearTimeout(timeoutId);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const matches = data.matches || [];
        const groups = data.groups || null;

        // Complete user's click: if exactly one flat match and no groups,
        // advance to tire size picker. Only fires when the user explicitly clicked
        // a result or group — never from typing or Back button. (TRAC-378)
        if (userClicked && matches.length === 1 && !groups?.length) {
          const match = matches[0];
          setYmmvLoading(false);
          setYmmvSelectedVehicle(match);
          setYmmvStep("tire_size");
          fetchTireSizes(match, variant);
          return;
        }

        // Fallback: when called from a flat-match click (handleYmmvSelect)
        // and the search returned no groups, go directly to tire sizes with
        // the original match. Prevents infinite re-search loops (e.g., clicking
        // "1999 Chevrolet Corvette" returns Corvette + Corvette Convertible as
        // flat matches with no groups — without this, clicking it again loops).
        if (fallbackMatch && !groups?.length) {
          setYmmvLoading(false);
          setYmmvSelectedVehicle(fallbackMatch);
          setYmmvStep("tire_size");
          fetchTireSizes(fallbackMatch, variant);
          return;
        }

        setYmmvResults(matches);
        setYmmvGroups(groups);
        setYmmvTotalCount(data.total_count || 0);
        setYmmvGroupType(data.group_type || null);
        setYmmvMultiTier(data.multi_tier || false);
        setYmmvTotalMatches(data.total_matches || 0);
        setYmmvDroppedYear(data.dropped_year || null);
        setYmmvSelectedIndex(-1);
        setYmmvLoading(false);
      } catch {
        if (attempt < MAX_RETRIES - 1) {
          return searchYMMV(q, variant, attempt + 1, fallbackMatch, userClicked);
        }
        // Final failure — silently fail, user can keep typing
        setYmmvLoading(false);
      }
    }, [fetchTireSizes]);

    /** Fetch all tire sizes from the backend (once per session, cached in ref) */
    const fetchAllTireSizes = useCallback(async () => {
      if (allTireSizesRef.current) return; // Already cached
      try {
        const res = await fetch(`${getBackendApiUrl()}/api/ymmv/all-tire-sizes`);
        if (res.ok) {
          const data = await res.json();
          allTireSizesRef.current = data.sizes || [];
        }
      } catch {
        // Silently fail — smart search degrades to no suggestions
      }
    }, []);

    /** Activate YMMV mode, optionally pre-filling the search input */
    const activateYmmvMode = useCallback((
      prefill?: string,
      source: "starter" | "agent" | "user" | "card" = "starter",
      // TRAC-896 dismissal-resume: reopen the SCREEN the customer backed out
      // of. 'tire_size' restores the selected vehicle and lands on its size
      // list; the vehicle step needs no special handling — the prefill IS the
      // query, and searching it rebuilds the same results.
      resume?: { step: "vehicle" | "tire_size"; vehicle?: YMMVMatch | null; variant?: string | null },
    ) => {
      ymmvSourceRef.current = source;
      // TRAC-1124: when the agent's reply opens the vehicle picker on a phone, the box
      // is not focused, so the keyboard the customer just sent away does not pop back
      // up over the reply. They tap the box to type a vehicle.
      // TRAC-1175 (keyboard rule 25): the same for every open on a phone, a starter chip,
      // a card or the header bar too, and a keyboard that is up closes when the customer
      // tapped to open it (an agent open leaves a box they are typing in alone). The
      // picker's year and make lists are tapped, not typed. Read once, before the blur,
      // since the blur flips the loader's keyboard flag. Desktop still focuses the box.
      const phone = isTouchPhone();
      if (phone && source !== "agent") dismissPhoneKeyboard(inputRef.current);
      const focusOnOpen = !phone;
      ymmvPrefillRef.current = prefill || "";
      ymmvDismissedRef.current = false; // Reset so dismiss message can fire once
      // Fetch all tire sizes in parallel (no-op if already cached)
      fetchAllTireSizes();
      // TRAC-232: Start YMMV timer when overlay opens
      ymmvStartTimeRef.current = Date.now();
      setYmmvElapsed(null);
      setYmmvMode(true);
      setYmmvStep("vehicle");
      setYmmvSelectedVehicle(null);
      setYmmvTireSizeData(null);
      setYmmvTireSizeLoading(false);
      setYmmvTireSizeError(false);
      setYmmvResults([]);
      setYmmvGroups(null);
      setYmmvSelectedIndex(-1);
      setYmmvTotalCount(0);
      setYmmvGroupType(null);
      setYmmvMultiTier(false);
      setYmmvTotalMatches(0);
      setYmmvDroppedYear(null);
      setYmmvActiveVariant(null);
      ymmvDrillStackRef.current = [];
      setYmmvCanGoBack(false);
      // Remove the current-turn assistant message to avoid a ghost bubble.
      // The ymmv_interface event arrives mid-stream, so the message may still
      // be streaming OR already finalized. Removing and persisting covers both.
      // TRAC-896: "card" is deliberately excluded — the partial-card flow KEEPS
      // the turn's answer text (the card sits inside that very bubble).
      if (source === "agent") {
        setMessages((prev) => {
          const lastAssistantIdx = prev.map((m) => m.role).lastIndexOf("assistant");
          if (lastAssistantIdx >= 0) {
            const filtered = prev.filter((_, i) => i !== lastAssistantIdx);
            chatAgentService.saveMessagesToSession(filtered);
            return filtered;
          }
          return prev;
        });
      }
      setIsStreaming(false);
      setIsLoading(false);
      if (resume?.step === "tire_size" && resume.vehicle) {
        setYmmvSelectedVehicle(resume.vehicle);
        setYmmvStep("tire_size");
        if (resume.variant) setYmmvActiveVariant(resume.variant);
        setInputValue(resume.vehicle.display);
        fetchTireSizes(resume.vehicle, resume.variant ?? undefined);
        if (focusOnOpen) setTimeout(() => inputRef.current?.focus({ preventScroll: true }), ymmvOpenFocusDelayMs());
        return;
      }
      if (prefill) {
        setInputValue(prefill);
      } else {
        setInputValue("");
      }
      // Trigger immediate search (empty → browse years, prefill → search)
      searchYMMV(prefill || "");
      // Focus input after a tick so the textarea is visible.
      // TRAC-896: when the card→interface morph is running, the input is riding
      // an overlay transform — focusing at 50ms would raise a mobile keyboard
      // mid-flight and fight the animation. ymmvOpenFocusDelayMs() holds until
      // the panel has essentially landed (~85%), and returns the original 50ms
      // whenever no morph is in play (reduced motion, starter/agent/user opens).
      if (focusOnOpen) setTimeout(() => inputRef.current?.focus({ preventScroll: true }), ymmvOpenFocusDelayMs());
    }, [searchYMMV, fetchAllTireSizes, fetchTireSizes]);

    /** Exit YMMV mode and reset state */
    const deactivateYmmvMode = useCallback(() => {
      setYmmvMode(false);
      setYmmvStep("vehicle");
      setYmmvSelectedVehicle(null);
      setYmmvTireSizeData(null);
      setYmmvTireSizeLoading(false);
      setYmmvTireSizeError(false);
      setYmmvResults([]);
      setYmmvGroups(null);
      setYmmvLoading(false);
      setYmmvSelectedIndex(-1);
      setYmmvTotalCount(0);
      setYmmvGroupType(null);
      setYmmvMultiTier(false);
      setYmmvTotalMatches(0);
      setYmmvActiveVariant(null);
      ymmvDrillStackRef.current = [];
      setYmmvCanGoBack(false);
      ymmvStartTimeRef.current = null;
      setInputValue("");
      if (ymmvDebounceRef.current) clearTimeout(ymmvDebounceRef.current);
    }, []);

    /** Handle vehicle selection from YMMV typeahead.
     *  Routes through searchYMMV to detect sub-models/variants before
     *  committing to tire sizes. Passes the original match as fallback
     *  so if no groups are found, we go to tire sizes directly (avoids
     *  infinite re-search when prefix siblings exist). */
    const handleYmmvSelect = useCallback((match: YMMVMatch) => {
      setInputValue(match.display);
      setYmmvSelectedIndex(-1);
      setYmmvActiveVariant(null);
      searchYMMV(match.display, undefined, 0, match, true);
    }, [searchYMMV]);

    /** Retry fetching tire sizes for the current vehicle */
    const handleTireSizeRetry = useCallback(() => {
      if (ymmvSelectedVehicle) {
        fetchTireSizes(ymmvSelectedVehicle, ymmvActiveVariant);
      }
    }, [fetchTireSizes, ymmvSelectedVehicle, ymmvActiveVariant]);

    /** Handle tire size selection from step 2 → close overlay and send combined message */
    const handleTireSizeSelect = useCallback((vehicle: YMMVMatch, tireSize: string, shopUrl?: string) => {
      // TRAC-232: Capture YMMV elapsed time in ms (overlay open → tire size committed)
      if (ymmvStartTimeRef.current) {
        const elapsedMs = Date.now() - ymmvStartTimeRef.current;
        setYmmvElapsed(elapsedMs);
        pendingYmmvTimingRef.current = { elapsedMs, openedAt: ymmvStartTimeRef.current };
      }
      // TRAC-232 / TRAC-965: the Adobe "fitment lookup" link now goes out through the
      // in-frame announcement in ChatInterface's onSelectionCommitted (postCapability →
      // trackAIEvent). The old aiTireAgentResponse push was retired here so a fitment answer
      // does not send "tr: ai tire agent: response" twice.
      const source = ymmvSourceRef.current;
      const variant = ymmvActiveVariant || undefined;
      // TRAC-896: a tap on the partial YMMV card ("card") is still an AGENT-raised picker
      // for completion purposes — same structured resume, same skipped user bubble.
      const agentRaised = source === "agent" || source === "card";
      // A check_fitment picker is a fitment QUESTION ("does 265/40R21 fit?") — the size is a query,
      // NOT the customer's tire. Commit the VEHICLE (real, and useful for follow-ups) but never the
      // queried size: it must not become "their tire" or a Shop-Tires target. The size still flows
      // to the agent's fit check via selectorCompletion below. Shopping intents commit the size.
      const isFitmentQuery = agentRaised && ymmvIntentRef.current === "check_fitment";
      deactivateYmmvMode();
      // TRAC-277: Set active vehicle from YMMV selection (include variant for Shop Tires URL)
      // TRAC-319: Skip appending variant when it's a pure tire-size string (e.g. "LT285/70-17")
      // — redundant with the tire size already shown in the header bar.
      const isPureSizeVariant = variant ? /^[A-Z]{0,2}\d+\/\d+-\d+$/.test(variant) : false;
      const displayWithVariant = (variant && !isPureSizeVariant) ? `${vehicle.display} ${variant}` : vehicle.display;
      // Parse staggered YMMV format: "275/35R22 (front) / 315/30R22 (rear)"
      const staggeredYmmvMatch = tireSize.match(/^(\S+)\s*\(front\)\s*\/\s*(\S+)\s*\(rear\)$/i);
      const ymmvYear = parseInt(vehicle.year, 10);
      const ymmvVehicle: ActiveVehicle = {
        id: vehicleIdentityKey(ymmvYear, vehicle.make, vehicle.model, variant),
        description: displayWithVariant,
        year: ymmvYear,
        make: vehicle.make,
        model: vehicle.model,
        variant,
        tireSize: isFitmentQuery ? undefined : (staggeredYmmvMatch ? undefined : tireSize),
        staggered: isFitmentQuery ? undefined : (staggeredYmmvMatch ? true : undefined),
        frontSize: isFitmentQuery ? undefined : (staggeredYmmvMatch ? staggeredYmmvMatch[1] : undefined),
        rearSize: isFitmentQuery ? undefined : (staggeredYmmvMatch ? staggeredYmmvMatch[2] : undefined),
        shopUrl: isFitmentQuery ? undefined : shopUrl,
        source: "ymmv",
        timestamp: Date.now(),
      };
      upsertVehicle(ymmvVehicle);
      if (source === "user") {
        // Silent swap: update active vehicle + pending context, no message — and
        // therefore NO selection card (QA 8/13, "choose a new vehicle" looked like it
        // sent a message and then did nothing). The card's contract is "a record of a
        // choice already sent to the agent"; a silent swap sends nothing, so nothing
        // renders. The header updating is the whole feedback.
        setActiveVehicle(ymmvVehicle);
        pendingVehicleContextRef.current = ymmvVehicle;
        return;
      }
      // TRAC-896: tell the consumer what the customer settled on — every completion
      // BELOW here sends a message, so the user-slot selection card renders as its
      // record. Same vehicle string the header bar shows, so the two can never
      // disagree. The size is withheld for fitment questions — there it is a query,
      // not their tire.
      onSelectionCommitted?.(displayWithVariant, isFitmentQuery ? undefined : tireSize);
      // Update header immediately so user sees feedback before agent responds
      setActiveVehicle(ymmvVehicle);
      // TRAC-325: Use clean display (without variant suffix) for the agent message
      // so the orchestrator's combo pattern parser gets a clean model name.
      // TRAC-416: Append [VARIANT:] tag so the orchestrator can pass it as auto_mod_clar
      // to generate_tdg_link, preventing the ambiguous-vehicle loop.
      const variantTag = (variant && !isPureSizeVariant) ? `\n[VARIANT: ${variant}]` : '';
      const actualMsg = `I have a ${vehicle.display} with ${tireSize} tires. I'd like tire recommendations.${variantTag}`;
      const displayMsg = `Find me tires for my ${displayWithVariant} (${tireSize})`;
      // TRAC-896 (8/12): every completion renders the user-slot selection card
      // (onSelectionCommitted above), so the raw resume text must never ALSO
      // render — two user messages for one action reads as an echo. The agent
      // still receives actualMsg; only the display bubble is suppressed.
      const skipUser = true;
      // Slice 2 resume: hand the agent the structured pick (intent + vehicle + size) so it resumes
      // the PINNED capability and binds the picked vehicle deterministically (agentv2.resume),
      // instead of leaning on the NL message alone. Intent is what the agent tagged the picker with
      // when it raised it; a customer-opened picker carries none → the agent defaults to recommend.
      pendingSelectorCompletionRef.current = {
        intent: agentRaised ? ymmvIntentRef.current : undefined,
        vehicle: { year: vehicle.year, make: vehicle.make, model: vehicle.model, trim: variant },
        tireSize,
      };
      handleSendMessageRef.current?.(actualMsg, displayMsg, skipUser);
    }, [deactivateYmmvMode, setActiveVehicle, upsertVehicle, ymmvActiveVariant, onSelectionCommitted]);

    // Slice 2 fitment resume: when the AGENT raised the picker WITH a size already named
    // (ymmvPrefillSizeRef — "does 265/40R21 fit my Civic"), the customer only needs to pin the
    // VEHICLE. The moment the vehicle is resolved and we'd land on the tire-size step, auto-complete
    // with the named size instead of making them pick a factory size that would replace it — so the
    // resume checks 265/40R21, not the vehicle's factory fitment.
    useEffect(() => {
      if (ymmvStep === "tire_size"
          && (ymmvSourceRef.current === "agent" || ymmvSourceRef.current === "card")
          && ymmvPrefillSizeRef.current && ymmvSelectedVehicle) {
        handleTireSizeSelect(ymmvSelectedVehicle, ymmvPrefillSizeRef.current);
      }
    }, [ymmvStep, ymmvSelectedVehicle, handleTireSizeSelect]);

    /** Handle back to vehicle search from tire size step */
    const handleBackToVehicle = useCallback(() => {
      setYmmvStep("vehicle");
      setYmmvSelectedVehicle(null);
      setYmmvTireSizeData(null);
      setYmmvTireSizeError(false);
      setYmmvLoading(false);
      setYmmvSelectedIndex(-1);
      // TRAC-1175: no keyboard on a phone when the customer goes back to the vehicle list.
      const focusBox = !isTouchPhone();
      setTimeout(() => {
        if (focusBox) inputRef.current?.focus({ preventScroll: true });
        // Re-trigger search with whatever is in the input so results are fresh
        const currentQuery = inputRef.current?.value || '';
        if (currentQuery.trim()) {
          searchYMMV(currentQuery);
        }
      }, 50);
    }, [searchYMMV]);

    /** Handle group selection — refine the search with the group label */
    const handleYmmvGroupSelect = useCallback((group: YMMVGroup) => {
      // TRAC-443: record the view we're leaving so the Back button can return
      // to it. Only push for the *narrowing* branches that stay inside the
      // vehicle-search step; the no-variant advance leaves to the tire-size
      // step (which has its own Back), so pushing there would add a redundant
      // extra back-press. Capture the live input (clicks set it
      // programmatically) + the active variant.
      const pushDrillStep = () => {
        ymmvDrillStackRef.current.push({
          query: inputRef.current?.value ?? "",
          variant: ymmvActiveVariant,
        });
        setYmmvCanGoBack(true);
      };

      // Click-on-exact-match-with-variants: when the user clicks a model row
      // whose label already exactly matches what's in the input, and the row
      // says hasVariants=true, the standard "drill" branch below would re-run
      // the same search and land on the same screen. Treat the click as
      // commitment to that specific entry — fetch its variants from the
      // tire-sizes endpoint and render them as pill rows so the user can pick
      // one. (e.g. "2024 Tesla Model Y" → pill rows for each stored variant.)
      const currentInput = inputRef.current?.value ?? "";
      // Normalize the same way the backend does (lowercase, drop hyphens,
      // collapse whitespace) so "2024 tesla model y", "2024 Tesla Model Y",
      // and "2024  Tesla  Model-Y" all count as the same string.
      const normalizeForMatch = (s: string): string =>
        s.toLowerCase().replace(/-/g, '').replace(/\s+/g, ' ').trim();
      const isExactMatchWithVariants = (
        !!group.model &&
        !!group.year &&
        !!group.make &&
        group.hasVariants === true &&
        !group.variant &&
        normalizeForMatch(group.label) === normalizeForMatch(currentInput)
      );
      if (isExactMatchWithVariants) {
        pushDrillStep();
        setYmmvSelectedIndex(-1);
        setYmmvActiveVariant(null);
        setYmmvLoading(true);
        (async () => {
          try {
            const url = `${getBackendApiUrl()}/api/ymmv/tire-sizes`
              + `?year=${encodeURIComponent(group.year!)}`
              + `&make=${encodeURIComponent(group.make!)}`
              + `&model=${encodeURIComponent(group.model!)}`;
            const res = await fetch(url);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            const variants = Array.isArray(data?.variants) ? data.variants : [];
            if (variants.length === 0) {
              // No variants surfaced — fall through to terminal advance so
              // the user isn't stranded.
              const fallbackMatch: YMMVMatch = {
                year: group.year!,
                make: group.make!,
                model: group.model!,
                display: group.label,
                tire_sizes: [],
              };
              setYmmvLoading(false);
              setYmmvSelectedVehicle(fallbackMatch);
              setYmmvStep("tire_size");
              fetchTireSizes(fallbackMatch, undefined);
              return;
            }
            // Build variant pill rows mirroring the backend's variant-group
            // shape so the existing variant click handler works unchanged.
            const VARIANT_SEPARATOR = " • ";
            const pillRows: YMMVGroup[] = variants.map((v: { name: string; sizes?: unknown[] }) => ({
              label: `${group.label}${VARIANT_SEPARATOR}${v.name}`,
              yearRange: group.year!,
              count: Array.isArray(v.sizes) ? v.sizes.length : 0,
              variant: v.name,
              searchQuery: group.label,
            }));
            setYmmvResults([]);
            setYmmvGroups(pillRows);
            setYmmvGroupType('model');
            setYmmvTotalCount(pillRows.length);
            setYmmvMultiTier(true);
            setYmmvTotalMatches(pillRows.length);
            setYmmvDroppedYear(null);
            setYmmvLoading(false);
          } catch (err) {
            // On failure, degrade gracefully — drop into tire-size picker
            // with the synthesized match rather than leaving the user stuck.
            const fallbackMatch: YMMVMatch = {
              year: group.year!,
              make: group.make!,
              model: group.model!,
              display: group.label,
              tire_sizes: [],
            };
            setYmmvLoading(false);
            setYmmvSelectedVehicle(fallbackMatch);
            setYmmvStep("tire_size");
            fetchTireSizes(fallbackMatch, undefined);
          }
        })();
        return;
      }

      if (group.variant && group.searchQuery) {
        // Variant group: apply the variant filter without overwriting the
        // input. The label contains the variant separator (" • ") which
        // doesn't tokenize — pushing it into the input would make the search
        // return no matches (TRAC-443).
        //
        // Do NOT push a drill-step here: a variant click advances to the
        // tire-size step, whose own Back re-searches the input and lands back
        // on this variant list. Pushing would put *this* same variant view on
        // the stack, so the first step-1 Back would be a no-op and the user
        // would have to press Back twice to reach the model list. Leaving the
        // stack alone keeps the model list on top → one Back returns to it.
        setYmmvSelectedIndex(-1);
        setYmmvActiveVariant(group.variant);
        searchYMMV(group.searchQuery, group.variant, 0, undefined, true);
      } else if (group.model && !group.hasVariants) {
        // TRAC-443: a model row with no named variants is a terminal vehicle —
        // clicking it should go straight to the tire-size picker (all sizes),
        // exactly like TR.com's "Find my Tire Size". Re-searching its exact
        // label would dead-end (the backend can't narrow further, and a
        // variant-bearing prefix sibling keeps the result non-empty so the
        // single-match advance gate never fires). Build the match directly.
        //
        // Deliberately do NOT touch the input: the tire-size step hides it and
        // its banner is driven by selectedVehicle, so leaving the query as the
        // user typed it means the tire-size Back button returns them to the
        // exact list they came from (matches the variant-row behavior — without
        // this the bottom bar would silently change to the model label).
        const match: YMMVMatch = {
          year: group.year!,
          make: group.make!,
          model: group.model,
          display: group.label,
          tire_sizes: [],
        };
        setYmmvSelectedIndex(-1);
        setYmmvActiveVariant(null);
        setYmmvSelectedVehicle(match);
        setYmmvStep("tire_size");
        fetchTireSizes(match, undefined);
      } else {
        // Make/model group (model rows WITH variants narrow to the variant
        // rows; year/make/family rows drill one tier deeper): refine search.
        pushDrillStep();
        const refinedQuery = group.label;
        setInputValue(refinedQuery);
        setYmmvSelectedIndex(-1);
        setYmmvActiveVariant(null);
        searchYMMV(refinedQuery, undefined, 0, undefined, true);
      }
    }, [searchYMMV, fetchTireSizes, ymmvActiveVariant]);

    /** TRAC-443: step back one drill-down level in the vehicle search.
     *  Pops the breadcrumb stack, restores the prior query + variant, and
     *  re-runs the search — so the user never has to hand-delete text. */
    const handleYmmvStepBack = useCallback(() => {
      const prev = ymmvDrillStackRef.current.pop();
      setYmmvCanGoBack(ymmvDrillStackRef.current.length > 0);
      if (!prev) return;
      // If we'd stepped back into the tire-size view, stay on vehicle search.
      setYmmvStep("vehicle");
      setYmmvSelectedVehicle(null);
      setYmmvTireSizeData(null);
      setYmmvTireSizeError(false);
      setYmmvSelectedIndex(-1);
      setInputValue(prev.query);
      setYmmvActiveVariant(prev.variant);
      searchYMMV(prev.query, prev.variant || undefined, 0, undefined, false);
      // TRAC-1175: no keyboard on a phone when the customer steps back in the picker.
      if (!isTouchPhone()) setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 50);
    }, [searchYMMV]);

    /** Handle YMMV dismiss — exit mode; behavior depends on how YMMV was opened */
    const handleYmmvDismiss = useCallback(() => {
      const source = ymmvSourceRef.current;
      const prefill = ymmvPrefillRef.current;
      // TRAC-896: raise the closing morph's cover BEFORE the state update, so the
      // un-swap renders underneath it. Every user-initiated dismissal reaches
      // this one function, so they all animate identically. The snapshot is the
      // picker's state at the moment of the X — the consumer uses it to carry
      // the customer's progress back onto the card they opened (8/12: backing
      // out must not discard what they did in here).
      onDismissRequested?.({
        step: ymmvStep,
        vehicle: ymmvSelectedVehicle,
        variant: ymmvActiveVariant,
      });
      deactivateYmmvMode();

      if (source === "starter" || source === "user" || source === "card") {
        // Opened from starter prompt button or header bar "Choose new vehicle"
        // — the user initiated this, not the agent. Close silently.
        // TRAC-896: "card" too — the partial card was never a takeover, so an X
        // just returns to the conversation (the card stays in history). No
        // "No worries…" apology: with no auto-open there's nothing to apologize for.
        return;
      }

      // Don't show if a message is currently streaming in
      if (isStreaming) return;

      // Only show if the last assistant message isn't already the dismiss message
      setMessages((prev) => {
        const lastAssistant = [...prev].reverse().find((m) => m.role === "assistant");
        if (lastAssistant?.isClientOnly && lastAssistant?.content?.includes("pull up the vehicle search again")) {
          return prev;
        }

        const dismissMsg: Message = {
          id: Date.now().toString(),
          role: "assistant",
          content: "No worries! Whenever you're ready, I can pull up the vehicle search again.",
          timestamp: new Date(),
          isClientOnly: true,
          parts: [
            { type: "text", content: "No worries! Whenever you're ready, I can pull up the vehicle search again." },
            {
              type: "selection_cards",
              title: "",
              options: [
                { value: prefill ? `Get help choosing tires for my car (${prefill})` : "Get help choosing tires for my car", label: "Find my vehicle" },
              ],
            },
          ],
        };
        return [...prev, dismissMsg];
      });
    }, [deactivateYmmvMode, isStreaming, onDismissRequested, ymmvStep, ymmvSelectedVehicle, ymmvActiveVariant]);

    /** Handle input change in YMMV mode — debounced search */
    const handleYmmvInputChange = useCallback((value: string) => {
      // TRAC-698: allow the user to type any character (the box shows exactly what
      // they typed), but strip characters that can't appear in a make/model from the
      // query we actually search. The backend sanitizes too as the canonical guard.
      setInputValue(value);
      setYmmvSelectedIndex(-1);
      setYmmvActiveVariant(null);
      // TRAC-443: manual typing diverges from the click-driven breadcrumb, so
      // the Back stack no longer reflects where the user is — clear it.
      ymmvDrillStackRef.current = [];
      setYmmvCanGoBack(false);
      if (ymmvDebounceRef.current) clearTimeout(ymmvDebounceRef.current);
      ymmvDebounceRef.current = setTimeout(() => searchYMMV(sanitizeVehicleQuery(value)), 300);
    }, [searchYMMV]);

    /** Handle keyboard navigation in YMMV mode */
    const handleYmmvKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const itemCount = ymmvGroups && ymmvGroups.length > 0
        ? ymmvGroups.length
        : ymmvResults.length;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setYmmvSelectedIndex((prev) => (prev < itemCount - 1 ? prev + 1 : prev));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setYmmvSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (ymmvSelectedIndex >= 0) {
          if (ymmvGroups && ymmvGroups.length > 0) {
            handleYmmvGroupSelect(ymmvGroups[ymmvSelectedIndex]);
          } else if (ymmvResults[ymmvSelectedIndex]) {
            handleYmmvSelect(ymmvResults[ymmvSelectedIndex]);
          }
        }
      } else if (e.key === "Escape") {
        // TRAC-896: Escape is a user dismissal exactly like the X, so it takes the
        // identical route — same closing morph, same source-dependent messaging.
        // It used to call deactivateYmmvMode() directly, which is why it hard-cut
        // while the X animated.
        handleYmmvDismiss();
      }
    }, [ymmvGroups, ymmvResults, ymmvSelectedIndex, handleYmmvGroupSelect, handleYmmvSelect, handleYmmvDismiss]);

  /** Reset all YMMV state — called from handleClearSession */
  const resetYmmvState = useCallback(() => {
      setYmmvMode(false);
      setYmmvStep("vehicle");
      setYmmvSelectedVehicle(null);
      setYmmvTireSizeData(null);
      setYmmvTireSizeLoading(false);
      setYmmvResults([]);
      setYmmvGroups(null);
      setYmmvLoading(false);
      setYmmvSelectedIndex(-1);
      setYmmvTotalCount(0);
      setYmmvGroupType(null);
      setYmmvMultiTier(false);
      setYmmvTotalMatches(0);
      setYmmvDroppedYear(null);
      setYmmvActiveVariant(null);
      ymmvStartTimeRef.current = null;
      ymmvPrefillRef.current = "";
      if (ymmvDebounceRef.current) clearTimeout(ymmvDebounceRef.current);
  }, []);

  return {
    ymmvMode, ymmvResults, ymmvGroups, ymmvLoading, ymmvSelectedIndex,
    ymmvTotalCount, ymmvGroupType, ymmvMultiTier, ymmvTotalMatches,
    ymmvDroppedYear, ymmvStep, ymmvSelectedVehicle, ymmvTireSizeData,
    ymmvTireSizeLoading, ymmvTireSizeError, ymmvCanGoBack,
    pendingYmmvTimingRef, ymmvPrefillRef, allTireSizesRef,
    // Slice 2 (agent-v2): picker-resume plumbing — the shell's streaming loop writes the intent/
    // prefill-size refs on ymmv_interface chunks and consumes the completion in handleSendMessage.
    ymmvIntentRef, ymmvPrefillSizeRef, pendingSelectorCompletionRef,
    activateYmmvMode, resetYmmvState, handleYmmvSelect, handleYmmvGroupSelect,
    handleTireSizeSelect, handleTireSizeRetry, handleBackToVehicle,
    handleYmmvStepBack, handleYmmvDismiss, handleYmmvInputChange, handleYmmvKeyDown,
  };
}
